import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Eye, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { eur } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import QuickView from "./QuickView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductCard({ product, index, wide = false }) {
    const [size, setSize] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);
    const [loading, setLoading] = useState(false);
    const [quickOpen, setQuickOpen] = useState(false);
    const [cgv, setCgv] = useState(false);
    const { addItem } = useCart();
    const tid = product.slug;
    const touchX = useRef(null);

    const prevImg = () => setImgIdx((i) => (i - 1 + product.gallery.length) % product.gallery.length);
    const nextImg = () => setImgIdx((i) => (i + 1) % product.gallery.length);

    const onTouchStart = (e) => {
        touchX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(delta) > 40) (delta < 0 ? nextImg : prevImg)();
        touchX.current = null;
    };

    const buy = async () => {
        if (!size) {
            toast.error("Choisis ta taille d'abord");
            return;
        }
        if (!cgv) {
            toast.error("Accepte les Conditions Générales de Vente pour continuer");
            return;
        }
        try {
            setLoading(true);
            const res = await fetch(`${API}/payments/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lookup_key: product.lookup_key,
                    quantity: 1,
                    origin_url: window.location.origin,
                    size,
                }),
            });
            const data = await res.json();
            if (!data.checkout_url) throw new Error(data.detail || "Erreur de paiement");
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error("Impossible de démarrer le paiement. Réessaie.");
            setLoading(false);
        }
    };

    const addToCart = () => {
        if (!size) {
            toast.error("Choisis ta taille d'abord");
            return;
        }
        addItem(product, size);
        toast.success(`${product.name} (${size}) ajouté au panier`);
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className={`group relative bg-spidey-card border border-zinc-800/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-spidey-red/60 hover:shadow-[0_0_45px_rgba(230,0,38,0.22)] h-full ${wide ? "md:col-span-2" : "flex flex-col"}`}
            data-testid={`product-card-${tid}`}
        >
            <div className={wide ? "grid md:grid-cols-2 items-center" : "contents"}>
                <div
                    className={`relative overflow-hidden bg-spidey-panel ${wide ? "aspect-square md:rounded-r-2xl" : "aspect-square"}`}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <motion.img
                        key={imgIdx}
                        src={product.gallery[imgIdx]}
                        alt={product.name}
                        initial={{ opacity: 0.35 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {product.gallery.length > 1 && (
                        <>
                            <button
                                onClick={prevImg}
                                aria-label="Photo précédente"
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/70 backdrop-blur border border-zinc-700 text-white flex items-center justify-center transition-all hover:border-spidey-red hover:text-spidey-neon md:opacity-0 md:group-hover:opacity-100"
                                data-testid={`prev-photo-${tid}`}
                            >
                                <ChevronLeft size={17} />
                            </button>
                            <button
                                onClick={nextImg}
                                aria-label="Photo suivante"
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/70 backdrop-blur border border-zinc-700 text-white flex items-center justify-center transition-all hover:border-spidey-red hover:text-spidey-neon md:opacity-0 md:group-hover:opacity-100"
                                data-testid={`next-photo-${tid}`}
                            >
                                <ChevronRight size={17} />
                            </button>
                        </>
                    )}
                    <div className="absolute top-4 left-4 flex gap-2">
                        <span className="bg-spidey-red text-white font-mono-brand text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                            {product.tag}
                        </span>
                    </div>
                    <button
                        onClick={() => setQuickOpen(true)}
                        className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/85 backdrop-blur border border-zinc-700 text-white text-xs font-mono-brand uppercase tracking-wider px-4 py-2 rounded-full opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all hover:border-spidey-red hover:text-spidey-neon"
                        data-testid={`quick-view-${tid}`}
                    >
                        <Eye size={14} />
                        Aperçu rapide
                    </button>
                    {product.gallery.length > 1 && (
                        <div className="absolute bottom-4 left-4 flex gap-2">
                            {product.gallery.map((g, i) => (
                                <button
                                    key={i}
                                    onClick={() => setImgIdx(i)}
                                    aria-label={`Photo ${i + 1}`}
                                    className={`w-2.5 h-2.5 rounded-full transition-all ${i === imgIdx ? "bg-spidey-red scale-110" : "bg-zinc-600 hover:bg-zinc-400"}`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className={`flex flex-col flex-1 p-6 sm:p-7 ${wide ? "md:p-10" : ""}`}>
                    <h3 className={`font-bold text-white leading-snug ${wide ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`}>
                        {product.name}
                    </h3>
                    <p className="mt-1 text-sm text-zinc-500">{product.subtitle}</p>

                    <div className="mt-5 flex items-baseline gap-3">
                        <span className={`text-white ${wide ? "font-display text-4xl" : "font-display text-3xl"}`}>{eur(product.price)}</span>
                        {product.original_price > product.price && (
                            <>
                                <span className="text-sm text-zinc-600 line-through">{eur(product.original_price)}</span>
                                <span className="bg-spidey-blood/40 text-spidey-neon font-mono-brand text-[10px] uppercase tracking-wider px-2 py-1 rounded">
                                    -{Math.round((1 - product.price / product.original_price) * 100)}%
                                </span>
                            </>
                        )}
                    </div>

                    <div className="mt-6">
                        <p className="text-[11px] font-mono-brand uppercase tracking-[0.2em] text-zinc-500 mb-2.5">
                            Taille {size ? `— ${size}` : ""}
                        </p>
                        <div className="grid grid-cols-3 gap-2" data-testid={`size-select-${tid}`}>
                            {product.sizes.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setSize(s)}
                                    className={`text-xs font-mono-brand py-2.5 rounded-lg border transition-all ${
                                        size === s
                                            ? "bg-spidey-red border-spidey-red text-white glow-red"
                                            : "border-zinc-700 text-zinc-400 hover:border-spidey-red/60 hover:text-white"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <ul className="mt-5 space-y-1.5 flex-1">
                        {product.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-[13px] text-zinc-400">
                                <Check size={14} className="text-spidey-red mt-0.5 shrink-0" />
                                {f}
                            </li>
                        ))}
                    </ul>

                    <label className="mt-5 flex items-start gap-2.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={cgv}
                            onChange={(e) => setCgv(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-[#E60026]"
                            data-testid={`cgv-checkbox-${tid}`}
                        />
                        <span className="text-xs text-zinc-500 leading-relaxed">
                            J'ai lu et j'accepte les{" "}
                            <a
                                href="/infos/cgv"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-spidey-neon underline underline-offset-2"
                            >
                                Conditions Générales de Vente
                            </a>
                        </span>
                    </label>

                    <button
                        onClick={buy}
                        disabled={loading}
                        className="mt-6 w-full flex items-center justify-center gap-2 bg-spidey-red hover:bg-spidey-neon disabled:opacity-70 text-white font-bold uppercase tracking-wider text-sm py-4 rounded-full transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(230,0,38,0.5)]"
                        data-testid={`buy-now-button-${tid}`}
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Redirection vers le paiement...
                            </>
                        ) : (
                            `Acheter — ${eur(product.price)}`
                        )}
                    </button>

                    <button
                        onClick={addToCart}
                        className="mt-3 w-full flex items-center justify-center gap-2 border border-zinc-700 hover:border-spidey-red/70 text-zinc-300 hover:text-white font-bold uppercase tracking-wider text-xs py-3.5 rounded-full transition-all"
                        data-testid={`add-to-cart-button-${tid}`}
                    >
                        <ShoppingBag size={14} />
                        Ajouter au panier
                    </button>
                </div>
            </div>

            <QuickView product={product} open={quickOpen} onClose={() => setQuickOpen(false)} />
        </motion.article>
    );
}
